import { Link } from "react-router-dom";
import * as Lucide from "lucide-react";
import { Seo } from "@/components/Seo";
import { SectionHead, CtaBand, Eyebrow } from "@/components/Shared";
import { BRAND, CITY_COUNT_LABEL, SERVICES, PROVINCES, CITIES, SECTORS, INSIGHTS, SEO_KEYWORDS } from "@/data/site";
import { url, metaHome, orgLd, websiteLd, citiesInProv } from "@/data/content";

const HERO_IMG = "https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxzZWN1cml0eSUyMGd1YXJkJTIwdW5pZm9ybWVkJTIwb2ZmaWNlciUyMG9mZmljZXIlMjBzYWZldHl8ZW58MHx8fHwxNzg5MjQ4ODM2fDA&ixlib=rb-4.1.0&q=85";

const Icon = ({ name, className }) => {
  const C = Lucide[name] || Lucide.Shield;
  return <C className={className} />;
};

const TRUST = [
  { icon: "Sun", t: "100% Indigenous-Owned & Operated" },
  { icon: "BadgeCheck", t: "Licensed Across All Provinces & Territories" },
  { icon: "Radio", t: `24/7 Emergency Dispatch: ${BRAND.phone}` },
  { icon: "ShieldCheck", t: "Fully Insured, Supervised & GPS-Verified" },
];

const STATS = [
  { n: "14", l: "Core Security Services" },
  { n: "13", l: "Provinces & Territories" },
  { n: CITY_COUNT_LABEL, l: "Canadian Cities Served" },
  { n: "24/7", l: "Live Guard Dispatch" },
];

export default function Home() {
  const meta = metaHome();
  const featuredCities = ["toronto", "montreal", "vancouver", "calgary", "edmonton", "ottawa", "winnipeg", "halifax", "quebec-city", "saskatoon", "thunder-bay", "regina"];
  return (
    <div data-testid="home-page">
      <Seo title={meta.title} description={meta.description} path="/" jsonLd={[orgLd(), websiteLd()]} />

      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, rgba(245,158,11,0.13) 0%, transparent 65%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <Eyebrow>First Nation Owned & Operated — Coast to Coast to Coast</Eyebrow>
            <h1 data-testid="hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] font-display text-slate-50">
              First Nation Owned. <span className="text-amber-400">Nationally Trusted</span> Security Guard Services.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl">
              Elite uniformed security guards, mobile patrols, concierge and specialized protection across every Canadian province and {CITY_COUNT_LABEL} cities — supervised, GPS-verified and dispatched 24/7.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={BRAND.phoneHref} data-testid="hero-call-cta" className="inline-flex items-center gap-2.5 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-slate-950 shadow-lg shadow-amber-500/25 transition-all hover:bg-amber-400 hover:-translate-y-0.5">
                <Lucide.Phone className="h-4 w-4" /> Call Dispatch: {BRAND.phone}
              </a>
              <a href={BRAND.emailHref} data-testid="hero-email-cta" className="inline-flex items-center gap-2.5 rounded-full border border-slate-600 bg-slate-800/60 px-7 py-3.5 text-sm font-semibold text-slate-100 transition-all hover:border-amber-500/60 hover:text-amber-400 hover:-translate-y-0.5">
                <Lucide.Mail className="h-4 w-4" /> {BRAND.email}
              </a>
              <Link to={url.locations()} data-testid="hero-coverage-cta" className="inline-flex items-center gap-2 px-4 py-3.5 text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors">
                Explore Coverage by City <Lucide.ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <ul className="mt-10 grid sm:grid-cols-2 gap-3">
              {TRUST.map((b) => (
                <li key={b.t} className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.03] px-4 py-3 text-sm text-slate-300">
                  <Icon name={b.icon} className="h-4 w-4 text-amber-400 shrink-0" /> {b.t}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-amber-500/10 blur-2xl" />
            <img src={HERO_IMG} alt="Uniformed security guard on duty in Canada" className="relative rounded-2xl border border-amber-500/30 object-cover w-full h-[420px] lg:h-[520px] shadow-2xl" loading="eager" />
            <div className="absolute -bottom-6 -left-6 rounded-2xl border border-white/10 bg-slate-900/90 backdrop-blur-xl px-6 py-4 shadow-2xl">
              <p className="font-mono text-2xl font-bold text-amber-400">1,000+</p>
              <p className="text-xs text-slate-400 uppercase tracking-widest">Protected sites & posts across Canada</p>
            </div>
          </div>
        </div>
        <div className="border-t border-white/5 bg-slate-900/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s) => (
              <div key={s.l} data-testid={`stat-${s.l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <p className="font-mono text-3xl sm:text-4xl font-bold text-amber-400">{s.n}</p>
                <p className="mt-1 text-xs uppercase tracking-widest text-slate-400">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20">
        <SectionHead eyebrow="Why Indigenous Security Services" title="Security built on trust, community and discipline" desc="We recruit locally, license rigorously and supervise relentlessly — because a guard you trust starts with a company that earns it." />
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: "Sun", t: "First Nation-Owned", d: "Proudly Indigenous-owned and operated. We hire from the communities we protect, delivering cultural safety alongside physical security." },
            { icon: "BadgeCheck", t: "Licensed & Fully Insured", d: "Every officer holds a valid provincial licence with background screening, full liability insurance and workers' compensation coverage." },
            { icon: "Radar", t: "Supervised & Verified", d: "Field supervisors, unannounced inspections, GPS-verified patrol logs and digital incident reports on every contract." },
          ].map((c) => (
            <div key={c.t} className="rounded-2xl border border-slate-800 bg-[#111827] p-7 transition-all hover:border-amber-500/40 hover:-translate-y-1">
              <div className="h-11 w-11 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-5">
                <Icon name={c.icon} className="h-5 w-5 text-amber-400" />
              </div>
              <h3 className="text-lg font-bold font-display text-slate-50 mb-2">{c.t}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#FAF9F6] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <SectionHead light eyebrow="Security Guard Services Canada" title="14 professional security services, one national standard" desc="From uniformed guards to K9 detection units, every service is delivered by licensed, insured and supervised professionals." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="services-grid">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={url.service(s)} data-testid={`service-card-${s.slug}`} className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:border-amber-500 hover:shadow-xl hover:-translate-y-1">
                <div className="h-11 w-11 rounded-xl bg-amber-500/10 flex items-center justify-center mb-4 transition-colors group-hover:bg-amber-500">
                  <Icon name={s.icon} className="h-5 w-5 text-amber-600 transition-colors group-hover:text-slate-950" />
                </div>
                <h3 className="font-display font-bold text-slate-900 mb-2 leading-snug">{s.name}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">{s.short}</p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-amber-600">
                  Learn more <Lucide.ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20">
        <SectionHead eyebrow="Nationwide Coverage" title="Security guards in every province, territory and major city" desc="Select your province to explore city-level coverage, or jump straight to your city below." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="provinces-selector">
          {PROVINCES.map((p) => (
            <Link key={p.slug} to={url.prov(p)} data-testid={`province-card-${p.slug}`} className="group rounded-xl border border-slate-800 bg-[#111827] p-5 transition-all hover:border-amber-500/50 hover:-translate-y-1">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display font-bold text-slate-100 group-hover:text-amber-400 transition-colors">{p.name}</h3>
                <span className="font-mono text-xs text-amber-500/80">{p.abbr}</span>
              </div>
              <p className="text-xs text-slate-500">{citiesInProv(p.slug).length} cities covered</p>
            </Link>
          ))}
        </div>
        <div className="mt-10">
          <h3 className="text-sm font-mono uppercase tracking-widest text-amber-500 mb-4">Featured Cities</h3>
          <div className="flex flex-wrap gap-2.5">
            {featuredCities.map((slug) => {
              const c = CITIES.find((x) => x.slug === slug);
              return (
                <Link key={slug} to={url.city(c)} data-testid={`city-chip-${slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-300 transition-all hover:border-amber-500/60 hover:text-amber-400">
                  <Lucide.MapPin className="h-3.5 w-3.5 text-amber-500/70" /> Security Guards {c.name}
                </Link>
              );
            })}
            <Link to={url.locations()} className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 border border-amber-500/40 px-4 py-2 text-sm font-semibold text-amber-400 hover:bg-amber-500/20 transition-colors">
              View all {CITY_COUNT_LABEL} cities <Lucide.ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      <section className="border-y border-white/5 bg-slate-900/40 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <SectionHead eyebrow="Industries We Protect" title="Sector-specific security programs" desc="Every industry has its own risk profile. Our programs are built sector by sector, site by site." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {SECTORS.map((x) => (
              <Link key={x.slug} to={url.sector(x)} data-testid={`sector-card-${x.slug}`} className="group rounded-xl border border-slate-800 bg-[#111827] p-5 transition-all hover:border-amber-500/50 hover:-translate-y-1">
                <Icon name={x.icon} className="h-5 w-5 text-amber-400 mb-3" />
                <h3 className="text-sm font-bold font-display text-slate-100 group-hover:text-amber-400 transition-colors leading-snug">{x.name}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20">
        <SectionHead eyebrow="Insights & Guides" title="Security industry intelligence for Canadian buyers" desc="Practical guides on licensing, loss prevention, fire watch compliance and hiring security guards in Canada." />
        <div className="grid md:grid-cols-3 gap-6" data-testid="insights-preview">
          {INSIGHTS.slice(0, 3).map((a) => (
            <Link key={a.slug} to={url.article(a)} data-testid="seo-insights-card" className="group rounded-2xl border border-slate-800 bg-[#111827] p-7 transition-all hover:border-amber-500/40 hover:-translate-y-1">
              <div className="flex items-center gap-2 mb-4">
                {a.tags.map((t) => (
                  <span key={t} className="rounded-full bg-amber-500/10 border border-amber-500/30 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest text-amber-400">{t}</span>
                ))}
              </div>
              <h3 className="font-display font-bold text-slate-100 leading-snug group-hover:text-amber-400 transition-colors mb-3">{a.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed mb-4">{a.excerpt}</p>
              <span className="text-xs font-mono text-slate-500">{a.minutes} min read</span>
            </Link>
          ))}
        </div>
        <div className="mt-8">
          <Link to={url.insights()} className="inline-flex items-center gap-2 text-sm font-semibold text-amber-400 hover:text-amber-300">
            Read all security guides <Lucide.ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-4">
        <div className="rounded-2xl border border-slate-800 bg-[#111827] p-8">
          <h2 className="text-sm font-mono uppercase tracking-widest text-amber-500 mb-4">Popular Security Searches in Canada</h2>
          <p className="text-sm text-slate-400 leading-relaxed mb-5">
            {BRAND.name} is a security guard company serving all of Canada. Common searches we answer every day include:
          </p>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {SEO_KEYWORDS.map((k) => (
              <Link key={k} to={url.services()} className="text-sm text-slate-400 hover:text-amber-400 transition-colors underline-offset-4 hover:underline">
                {k}
              </Link>
            ))}
          </div>
        </div>
        <CtaBand />
      </section>
    </div>
  );
}
